import React, { useRef, useState, useEffect } from "react";
import "./../../../../assets/css/PrescriptionTemplate.scss";
import SignatureCanvas from "react-signature-canvas";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import useTitanApp from "../../../../hooks/useTitanApp";
import { generatePatientPrescription } from "../../../../dispatchers/templateDispatchers";
import moment from "moment";
import { addVaccineDosage } from "../../../../dispatchers/nmsDispatchers";

const prescriptionData = {
  prescriptionDate: "06/02/2025",
  pharmacistRegInfo:
    "Use the link below to access the professional guidance and standard operating procedures that support the legislative changes within the Human Medicine Regulations 2012...",
  prescriptionLink: "tvrx.co.uk",
  patientDetails: {
    name: "Jennifer Turner",
    address: "123 Main Street, London, UK",
    dob: "12/04/1987",
    phone: "01234567890",
    email: "jennifer@example.com",
    dcaReference: "DCA12345",
    insuranceNumber: "",
  },
  appointmentDetails: {
    refNumber: "REF98765",
    dateTime: "06/02/2025 10:00 AM",
    clinician: "Dr. John Doe",
    nmc: "12345678",
  },
  prescriptionInfo: {
    medication: "OTOMIZE (Neomycin sulfate, acetic acid and dexamethasone)",
    formulation: "Spray",
    route: "Ear",
    dose: "One spray",
    frequency: "Three times a day for 7 days",
    duration: "7 days",
  },
  paymentInfo: "£21.54",
  authorizationCode: "DQA-1234-XXXX",
  pharmacistInstructions: [
    "Visit tvrx.co.uk",
    "Check ePrescription is valid for dispensing by entering the authorisation code",
    "If ePrescription is valid, dispense to the patient and press 'Mark as Dispensed'",
    "If ePrescription is invalid or has expired, do not dispense to patient",
  ],
  approvedPharmacies: [
    "Superdrug 03025992195",
    "Tesco Pharmacy 03025992195",
    "Boots 03025992195",
    "Day Lewis Pharmacy 03025992195",
    "John Bell Pharmacy 03025992195",
  ],
  signature: "Dr. John Doe",
};

const PrescriptionTemplate = ({ handleClosePrescription }) => {
  const { nmsServiceState } = useTitanApp();
  const { clinicalServiceId, responseDTOStagesData, selectedVaccineDose } =
    nmsServiceState || {};

  const sigCanvas = useRef(null);
  const prescriptionRef = useRef(null); // Reference for the entire page

  const [isCodeGenerated, setIsCodeGenerated] = useState(false);
  const [authorisationCode, setAuthorisationCode] = useState("");
  const [isAuthLoading, setIsAuthLoading] = useState(false);

  const processedData = responseDTOStagesData.reduce((acc, item) => {
    acc[item.name] = item.value;
    return acc;
  }, {});

  const getValueOrPlaceholder = (value) => {
    return value ?? "-";
  };

  const generatePrescriptionPDF = async () => {
    setIsAuthLoading(true);

    if (!prescriptionRef.current) return;

    try {
      const authorisationCode = Math.floor(
        100000 + Math.random() * 900000
      ).toString();

      if (authorisationCode) {
        setAuthorisationCode(authorisationCode);
        setIsCodeGenerated(true);
        setIsAuthLoading(false);
      }
    } catch (error) {
      console.error("Error generating authorisation code:", error);
    }
  };

  // Capture image after UI update
  useEffect(() => {
    if (isCodeGenerated) {
      capturePrescription();
    }
  }, [isCodeGenerated]);

  const capturePrescription = async () => {
    try {
      // Wait for UI to update
      await new Promise((resolve) => setTimeout(resolve, 100)); // Small delay to ensure UI updates

      // Capture the entire component
      const canvas = await html2canvas(prescriptionRef.current, {
        scale: 2,
        useCORS: true,
      });

      const imageData = canvas.toDataURL("image/png");

      // Create a new PDF document
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imageData, "PNG", 0, 0, imgWidth, imgHeight);
      // pdf.save("Prescription.pdf");

      // Calculate expiry date
      const currentDate = new Date();
      currentDate.setMonth(currentDate.getMonth() + 6);
      const expiryDate = currentDate.toISOString().split("T")[0];

      const formatDOBForAPI = (dobString) => {
        if (!dobString) return null;

        // Parse input date format (DD/MM/YYYY hh:mm:ss a)
        const [datePart, timePart, meridian] = dobString.split(" ");
        const [day, month, year] = datePart.split("/");

        // Convert to 24-hour format
        let [hours, minutes, seconds] = timePart.split(":");
        if (meridian?.toLowerCase() === "pm" && hours !== "12") {
          hours = String(Number(hours) + 12);
        } else if (meridian?.toLowerCase() === "am" && hours === "12") {
          hours = "00";
        }

        // Format to ISO string
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      };

      const prescriptionModel = {
        AuthorizationCode: authorisationCode,
        PrescriptionBase64: imageData,
        PrescriptionExpiryDate: expiryDate,
        PrescriptionStatus: 0,
        ClinicalServiceId: clinicalServiceId,
        LastName: getValueOrPlaceholder(processedData?.LastName),
        DOB: formatDOBForAPI(processedData?.DOB),
        Notes: "",
      };
      generatePatientPrescription(prescriptionModel);
      handleAddVaccines();
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };
  function handleAddVaccines() {
    const vaccineList = selectedVaccineDose
      ?.filter((dose) => dose?.id !== 1)
      ?.map((dose) => ({
        vaccineName: dose?.label,
        dose: dose?.id,
        isCompleted: false,
        doseDate: moment()
          .add(parseInt(dose?.time), "days")
          .utc()
          .toISOString(),
      }));

    if (vaccineList.length > 0) {
      addVaccineDosage({
        data: vaccineList,
        clinicalServiceID: clinicalServiceId,
      });
    }
  }

  return (
    <div className="prescription-template-container">
      <button className="close-popup-button" onClick={handleClosePrescription}>
        &times;
      </button>

      {/* Full Component Wrapped Inside ref */}
      <div className="prescription-template" ref={prescriptionRef}>
        <header>
          <h1>Private ePrescription ({prescriptionData.prescriptionDate})</h1>
        </header>

        <div className="prescription_content_section">
          <section className="pharmacist-info">
            <div className="pharmacist-info_content">
              <div className="info-label">Pharmacist</div>
              <div className="info-content">
                <p>
                  Use the link below to access the professional guidance and
                  standard operating procedures that support
                </p>
              </div>
            </div>
            <div className="pharmacist-info_content">
              <div className="info-label">Regulatory</div>
              <div className="info-content">
                <p>
                  the legislative changes within the Human Medicine Regulations
                  2012. Enabling the despensing of electronics.
                </p>
              </div>
            </div>
            <div className="pharmacist-info_content">
              <div className="info-label">Information</div>
              <div className="info-content">
                <p>
                  Private prescription :
                  <a
                    href={`https://${prescriptionData.prescriptionLink}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {prescriptionData.prescriptionLink}
                  </a>
                </p>
              </div>
            </div>
          </section>

          <section className="patient-appointment">
            <div className="patient-details">
              <h2>Patient Details</h2>
              <p>
                <strong>Name:</strong>
                {[
                  getValueOrPlaceholder(processedData?.Title),
                  getValueOrPlaceholder(processedData?.FirstName),
                  getValueOrPlaceholder(processedData?.LastName),
                ]
                  .filter((namePart) => namePart !== "-")
                  .join(" ") || "-"}
              </p>
              <p>
                <strong>Address:</strong>{" "}
                {getValueOrPlaceholder(processedData?.PatientAddress)}
              </p>
              <p>
                <strong>Date of Birth:</strong>{" "}
                {processedData.DOB
                  ? moment(processedData.DOB, "DD/MM/YYYY hh:mm:ss a").format(
                      "DD/MM/YYYY"
                    )
                  : "-"}
              </p>
              <p>
                <strong>Phone:</strong>
                {getValueOrPlaceholder(processedData?.PatientTelephoneNumber)}
              </p>
              <p>
                <strong>Email:</strong>{" "}
                {getValueOrPlaceholder(processedData?.Email)}
              </p>
              <p>
                <strong>DCA Reference No.:</strong>{" "}
                {prescriptionData.patientDetails.dcaReference}
              </p>
              <p>
                <strong>Insurance Member No.:</strong>{" "}
                {prescriptionData.patientDetails.insuranceNumber}
              </p>
            </div>

            <div className="appointment-details">
              <h2>Appointment Details</h2>
              <p>
                <strong>Ref Number:</strong>{" "}
                {prescriptionData.appointmentDetails.refNumber}
              </p>
              <p>
                <strong>Date & Time:</strong>{" "}
                {prescriptionData.appointmentDetails.dateTime}
              </p>
              <p>
                <strong>Clinician:</strong>{" "}
                {prescriptionData.appointmentDetails.clinician}
              </p>
              <p>
                <strong>NMC:</strong> {prescriptionData.appointmentDetails.nmc}
              </p>
            </div>
          </section>

          <section className="payment-section">
            <h2>Additional notes:</h2>
            <p>
              <strong>Allergies:</strong> No known drug allergies, nil
            </p>
          </section>

          <section className="prescription-info">
            <h2>Prescription Details</h2>
            <table>
              <thead>
                <tr>
                  <th>Medication</th>
                  <th>Formulation</th>
                  <th>Route</th>
                  <th>Dose</th>
                  <th>Frequency</th>
                  <th>Quantity/Duration</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{prescriptionData.prescriptionInfo.medication}</td>
                  <td>{prescriptionData.prescriptionInfo.formulation}</td>
                  <td>{prescriptionData.prescriptionInfo.route}</td>
                  <td>{prescriptionData.prescriptionInfo.dose}</td>
                  <td>{prescriptionData.prescriptionInfo.frequency}</td>
                  <td>{prescriptionData.prescriptionInfo.duration}</td>
                </tr>
              </tbody>
            </table>
          </section>

          <section className="signature-section">
            <div className="signature-box">
              <div>
                <p>
                  Jennifer Turner <strong>NMC:</strong>10B2025E
                </p>
                <p>
                  <strong>Signature:</strong>
                </p>
                <div className="signature-area">
                  <SignatureCanvas
                    ref={sigCanvas}
                    penColor="black"
                    // onEnd={handleSignature}
                    canvasProps={{
                      width: 400,
                      height: 100,
                      className: "signature-canvas",
                    }}
                  />

                  {/* {isVisible && (
                    <div>
                      <button onClick={clearSignature} className="delete-signature-btn">
                        <DeleteIcon />
                      </button>
                    </div>
                  )} */}
                </div>
                <hr></hr>
              </div>
              {isAuthLoading ? (
                <div className="authorization_box">
                  <p>
                    <strong>Generating...</strong>
                  </p>
                </div>
              ) : isCodeGenerated ? (
                <div className="authorization_box">
                  <p>
                    <strong>Authorisation Code: {authorisationCode}</strong>
                  </p>
                </div>
              ) : (
                <div
                  className="authorization_box"
                  onClick={generatePrescriptionPDF}
                >
                  <p>
                    <strong>Generate Precription</strong>
                  </p>
                </div>
              )}
            </div>
            <div className="pharmacist-instructions">
              <div>
                <h2>Pharmacist Instructions</h2>
                <ol className="numbered-list">
                  {prescriptionData.pharmacistInstructions.map(
                    (instruction, index) => (
                      <li key={index}>{instruction}</li>
                    )
                  )}
                </ol>
              </div>
              <div className="authorization_box">
                <p>
                  <strong>PHARMACISTS:</strong>
                </p>
                <p>
                  <strong>
                    Ensure you follow these steps for safe dispensing.
                  </strong>
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrescriptionTemplate;
